import { useState } from 'react';
import Spinner from '../common/Spinner';
import InvoiceLetterhead from './InvoiceLetterhead';
import InvoiceMetaGridEditable from './InvoiceMetaGridEditable';
import InvoiceItemsTableEditable from './InvoiceItemsTableEditable';
import InvoiceTotalsEditable from './InvoiceTotalsEditable';
import DailyLimitPanel from './DailyLimitPanel';
import BankDetailsSection from './BankDetailsSection';
import { calcTotals, todayStr } from '../../utils/formatters';

const blankItem = () => ({ description: '', qty: 1, unitPrice: 0, vatRate: 0, total: 0 });

export default function InvoiceEditor({ initial, type, dailyLimit, todaySalesExcludingSelf, saleDocumentTitle, onSave, onCancel }) {
  const [inv, setInv] = useState({
    supplier: '', address: '', phone: '', email: '', poNumber: '', date: todayStr(), delivery: 0,
    items: [blankItem()],
    ...(initial || {}),
    type,
    invoiceNo: initial?.invoiceNo || '',
  });
  const [saving, setSaving] = useState(false);

  const { subtotal, vatAmount, grandTotal } = calcTotals(inv.items, inv.delivery);

  function set(k, v) { setInv(p => ({ ...p, [k]: v })); }

  function updateItem(i, k, v) {
    const items = inv.items.map((it, j) => {
      if (j !== i) return it;
      const upd = { ...it, [k]: v };
      if (k === 'discount') {
        const disc = Number(v) || 0;
        const base = upd.matchedProductPrice || (Number(upd.unitPrice) + (Number(it.discount) || 0));
        upd.matchedProductPrice = base;
        upd.unitPrice = Math.max(0, parseFloat((base - disc).toFixed(2)));
      } else if (k === 'unitPrice' && upd.matchedProductPrice) {
        upd.discount = Math.max(0, parseFloat((upd.matchedProductPrice - Number(v)).toFixed(2)));
      }
      upd.total = Number(upd.qty) * Number(upd.unitPrice);
      return upd;
    });
    setInv(p => ({ ...p, items }));
  }

  function addItem() { setInv(p => ({ ...p, items: [...p.items, blankItem()] })); }
  function removeItem(i) { if (inv.items.length > 1) setInv(p => ({ ...p, items: p.items.filter((_, j) => j !== i) })); }

  function addFiller(product, qty) {
    setInv(prev => ({
      ...prev,
      items: [...prev.items, { description: product.name, qty, unitPrice: product.unitPrice, vatRate: product.vatRate, total: qty * product.unitPrice, isAutoAdded: false }],
    }));
  }

  async function save() {
    if (!inv.invoiceNo) { alert('Invoice number required'); return; }
    setSaving(true);
    try {
      const payload = { ...inv, subtotal, vatAmount, grandTotal };
      const result = await onSave(payload);
      if (result?.xlsxBase64) {
        const a = document.createElement('a');
        a.href = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + result.xlsxBase64;
        a.download = `${inv.invoiceNo}_${inv.date}.xlsx`;
        a.click();
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="invoice-sheet">
      <InvoiceLetterhead type={type} saleDocumentTitle={saleDocumentTitle} />
      <InvoiceMetaGridEditable type={type} invoice={inv} onChange={set} />
      <InvoiceItemsTableEditable
        items={inv.items}
        onUpdateItem={updateItem}
        onAddItem={addItem}
        onRemoveItem={removeItem}
      />
      <InvoiceTotalsEditable
        subtotal={subtotal}
        vatAmount={vatAmount}
        grandTotal={grandTotal}
        delivery={inv.delivery}
        onDeliveryChange={(v) => set('delivery', v)}
      />

      {type === 'sale' && <BankDetailsSection />}

      {type === 'sale' && (
        <DailyLimitPanel
          dailyLimit={dailyLimit}
          todaySalesExcludingSelf={todaySalesExcludingSelf}
          grandTotal={grandTotal}
          onAddFiller={addFiller}
        />
      )}

      <div className="editor-actions no-print">
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-outline" onClick={() => window.print()}>🖨 Print</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <><Spinner /> Saving & uploading...</> : '💾 Save & Export to Drive'}
        </button>
      </div>
    </div>
  );
}
